// Trusted networks (Phase 8, company_admin only): live partner calls are
// accepted only from the public addresses a company registered here and the
// platform approved, and only from Kenya unless the platform made an
// exception. The sandbox is never restricted.
import { Alert, Badge, Button, Group, Loader, Paper, Stack, Table, Text, TextInput, Title } from "@mantine/core";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useState, type FormEvent } from "react";
import { useTranslation } from "react-i18next";
import { api, type Schemas } from "../../api/client";
import { errorMessage, unwrap } from "../../shared/session/errors";
import { useFormat } from "../../shared/ui/format";
import { FormError, Notice } from "../../shared/ui/Page";
import { companyKey, ConfirmDialog } from "./common";

type Network = Schemas["TrustedNetwork"];

const STATUS_COLOUR: Record<Network["status"], string> = {
  pending: "yellow",
  approved: "green",
  rejected: "red",
  revoked: "gray",
};

/** looksLikeNetwork is a light check before the server's: an IPv4 or IPv6 address with an optional prefix length. */
export function looksLikeNetwork(v: string): boolean {
  const s = v.trim();
  const [addr = "", bits, extra] = s.split("/");
  if (extra !== undefined || (bits !== undefined && !/^\d{1,3}$/.test(bits))) return false;
  const v4 = /^(25[0-5]|2[0-4]\d|1?\d?\d)(\.(25[0-5]|2[0-4]\d|1?\d?\d)){3}$/.test(addr);
  const v6 = addr.includes(":") && /^[0-9a-fA-F:.]+$/.test(addr);
  if (!v4 && !v6) return false;
  return bits === undefined || Number(bits) <= (v4 ? 32 : 128);
}

export function NetworksTab({ companyId }: { companyId: string }) {
  const { t } = useTranslation();
  const f = useFormat();
  const queryClient = useQueryClient();
  const key = companyKey(companyId, "trusted-networks");
  const networks = useQuery({
    queryKey: key,
    queryFn: () =>
      unwrap(api.GET("/companies/{company_id}/trusted-networks", { params: { path: { company_id: companyId } } })),
  });
  const [cidr, setCidr] = useState("");
  const [label, setLabel] = useState("");
  const [adding, setAdding] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [removing, setRemoving] = useState<Network | null>(null);
  const [busy, setBusy] = useState(false);
  const [removeError, setRemoveError] = useState<string | null>(null);
  const items = networks.data?.items ?? [];
  const approved = items.filter((n) => n.status === "approved").length;

  const add = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    setNotice(null);
    if (!looksLikeNetwork(cidr)) return setError(t("networks.invalid"));
    setAdding(true);
    const { data, error } = await api.POST("/companies/{company_id}/trusted-networks", {
      params: { path: { company_id: companyId } },
      body: { cidr: cidr.trim(), ...(label.trim() ? { label: label.trim() } : {}) },
    });
    setAdding(false);
    if (!data) return setError(errorMessage(error, t("error.generic")));
    setCidr("");
    setLabel("");
    setNotice(
      data.in_kenya
        ? t("networks.requested_notice", { cidr: data.cidr })
        : t("networks.requested_abroad", { cidr: data.cidr }),
    );
    void queryClient.invalidateQueries({ queryKey: key });
  };

  const remove = async () => {
    if (!removing) return;
    setBusy(true);
    setRemoveError(null);
    const { error, response } = await api.DELETE("/companies/{company_id}/trusted-networks/{network_id}", {
      params: { path: { company_id: companyId, network_id: removing.id } },
    });
    setBusy(false);
    if (response.status !== 204) return setRemoveError(errorMessage(error, t("error.generic")));
    setNotice(t("networks.removed", { cidr: removing.cidr }));
    setRemoving(null);
    void queryClient.invalidateQueries({ queryKey: key });
  };

  return (
    <Stack gap="lg">
      <Alert color="brand" title={t("networks.rule_title")} data-testid="networks-rule">
        {t("networks.rule_body")}
      </Alert>
      <Paper withBorder p="md" radius="md" data-testid="trusted-networks">
        <Stack gap="md">
          <Group justify="space-between">
            <Title order={2} size="h4">
              {t("networks.title")}
            </Title>
            <Badge variant="default" data-testid="networks-approved">
              {t("networks.approved_count", { count: approved })}
            </Badge>
          </Group>
          {notice && <Notice>{notice}</Notice>}
          {networks.isPending ? (
            <Loader size="sm" aria-label={t("common.loading")} />
          ) : networks.isError ? (
            <FormError message={errorMessage(networks.error, t("error.generic"))} />
          ) : items.length === 0 ? (
            <Text size="sm" c="dimmed" data-testid="networks-empty">
              {t("networks.empty")}
            </Text>
          ) : (
            <Table.ScrollContainer minWidth={620}>
              <Table verticalSpacing="sm" aria-label={t("networks.title")}>
                <Table.Thead>
                  <Table.Tr>
                    <Table.Th>{t("networks.network")}</Table.Th>
                    <Table.Th>{t("networks.status")}</Table.Th>
                    <Table.Th>{t("networks.location")}</Table.Th>
                    <Table.Th>{t("networks.requested")}</Table.Th>
                    <Table.Th>{t("staff.actions")}</Table.Th>
                  </Table.Tr>
                </Table.Thead>
                <Table.Tbody>
                  {items.map((n) => (
                    <Table.Tr key={n.id} data-testid="network-row">
                      <Table.Td>
                        <Text size="sm" ff="monospace">
                          {n.cidr}
                        </Text>
                        {n.label && (
                          <Text size="xs" c="dimmed">
                            {n.label}
                          </Text>
                        )}
                      </Table.Td>
                      <Table.Td>
                        <Badge color={STATUS_COLOUR[n.status]} data-testid="network-status">
                          {t(`networks.status_${n.status}`)}
                        </Badge>
                      </Table.Td>
                      <Table.Td>
                        <Text size="sm">
                          {n.in_kenya
                            ? t("networks.in_kenya")
                            : n.outside_kenya
                              ? t("networks.exception")
                              : t("networks.outside_kenya")}
                        </Text>
                      </Table.Td>
                      <Table.Td>{f.date(n.requested_at)}</Table.Td>
                      <Table.Td>
                        {n.status !== "rejected" && (
                          <Button
                            size="xs"
                            variant="light"
                            color="red"
                            onClick={() => {
                              setRemoveError(null);
                              setRemoving(n);
                            }}
                            aria-label={t("networks.remove_for", { cidr: n.cidr })}
                          >
                            {t("staff.remove")}
                          </Button>
                        )}
                      </Table.Td>
                    </Table.Tr>
                  ))}
                </Table.Tbody>
              </Table>
            </Table.ScrollContainer>
          )}
        </Stack>
      </Paper>
      <Paper withBorder p="md" radius="md">
        <form onSubmit={(e) => void add(e)} noValidate>
          <Stack gap="sm">
            <Title order={2} size="h4">
              {t("networks.add_title")}
            </Title>
            <Text size="sm" c="dimmed">
              {t("networks.add_hint")}
            </Text>
            <Group align="flex-start" gap="sm" wrap="wrap">
              <TextInput
                label={t("networks.cidr")}
                description={t("networks.cidr_hint")}
                placeholder="41.90.12.0/24"
                value={cidr}
                onChange={(e) => setCidr(e.currentTarget.value)}
                autoComplete="off"
                spellCheck={false}
                ff="monospace"
                miw={240}
                required
              />
              <TextInput
                label={t("networks.label")}
                description={t("networks.label_hint")}
                value={label}
                onChange={(e) => setLabel(e.currentTarget.value)}
                maxLength={60}
                miw={240}
              />
            </Group>
            <FormError message={error} />
            <Button type="submit" w="fit-content" loading={adding}>
              {t("networks.add")}
            </Button>
          </Stack>
        </form>
      </Paper>
      <ConfirmDialog
        opened={removing !== null}
        title={t("networks.remove_title")}
        confirmLabel={t("staff.remove")}
        busy={busy}
        error={removeError}
        onConfirm={() => void remove()}
        onCancel={() => setRemoving(null)}
      >
        {t("networks.remove_body", { cidr: removing?.cidr ?? "" })}
      </ConfirmDialog>
    </Stack>
  );
}
